import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Zap } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        new_password: newPassword
      });
      
      toast.success('Password reset successful!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md fade-in">
        <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-2xl">
          <div className="flex flex-col items-center mb-8">
            <img src="/bess-scan-logo.png" alt="BESS SCAN" className="h-28 w-auto mb-3" />
            <h1 className="text-3xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>Reset Password</h1>
            <p className="text-gray-600 mt-2 text-center">Enter your email and new password</p>
          </div>

          <form onSubmit={handleReset} className="space-y-6" data-testid="reset-password-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white border-gray-300 text-black placeholder:text-gray-500 focus:border-red-500 h-12 rounded-xl"
                data-testid="reset-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-gray-700 font-medium">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="bg-white border-gray-300 text-black placeholder:text-gray-500 focus:border-red-500 h-12 rounded-xl"
                data-testid="reset-new-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-medium h-12 rounded-xl text-base"
              data-testid="reset-submit-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            Remember your password?{' '}
            <Link 
              to="/login" 
              className="text-red-500 hover:text-red-400 font-medium"
              data-testid="back-to-login-link"
            >
              Sign in
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="text-gray-600 hover:text-white text-sm inline-flex items-center space-x-2"
          >
            <span>←</span>
            <span>Back to home</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;